import { useContext, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { useOrganizationStaff } from "src/data/organization/queries/staff";
import { useOrganizationRoles } from "src/data/organization/queries/staffRole";
import {
  isOrganizationManageEnabled,
  isUnlimitedQuota,
} from "src/helpers/FeatureCapabilityConfig";
import {
  isStaffPoolFull,
  staffAllowanceFor,
} from "src/business/organization/staffAllowance";
import { InfoCard } from "src/design/InfoCard";
import { CustomButton } from "src/design/CustomButton";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextPrimary,
  colorTextMuted,
  colorAccent,
  colorSurfaceHover,
  colorTextError,
} from "src/design/tokens";
import { InviteStaffDialog } from "./components/InviteStaffDialog";

const StaffList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { restaurants } = useContext(RestaurantContext);
  const organizationId = user?.organizationId ?? null;

  const [search, setSearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const { data: staffList, isLoading } = useOrganizationStaff(organizationId);
  const { data: roles } = useOrganizationRoles();

  const staffCount = staffList?.length ?? 0;
  // Read from the tiers the restaurants hold rather than from a flat three per
  // restaurant: the reservation tier sells unlimited accounts, and counting it
  // as three showed an owner "1 / 3" against a plan with no ceiling.
  const maxStaff = staffAllowanceFor(restaurants);
  const atLimit = isStaffPoolFull(staffCount, maxStaff);

  const roleNameById = (roleId: string) =>
    roles?.find((r) => r.id === roleId)?.name ?? "—";

  const restaurantNamesByIds = (restaurantIds: string[]) => {
    const names = restaurantIds
      .map((id) => restaurants.find((r) => r.id === id)?.name)
      .filter((name): name is string => Boolean(name));
    return names.length > 0 ? names.join(", ") : "—";
  };

  const filtered = (staffList ?? []).filter((s) => {
    const fullName = `${s.firstName} ${s.lastName}`.toLowerCase();
    const q = search.toLowerCase();
    return fullName.includes(q) || s.email.toLowerCase().includes(q);
  });

  return (
    <>
      <Box>
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colorTextPrimary }}>
            {t("myOrganization.staff.pageTitle")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            placeholder={t("myOrganization.staff.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colorTextMuted, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <InfoCard
          title={`${t("myOrganization.staff.pageTitle")} (${filtered.length})`}
          icon={<PersonOutlineIcon fontSize="small" />}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: atLimit ? colorTextError : colorTextMuted,
                  whiteSpace: "nowrap",
                }}
              >
                {staffCount} /{" "}
                {isUnlimitedQuota(maxStaff)
                  ? t("myOrganization.staff.unlimited")
                  : maxStaff}
              </Typography>
              {isOrganizationManageEnabled(user) && (
                <CustomButton
                  color="black"
                  startIcon={<PersonAddAltIcon />}
                  disabled={atLimit || !organizationId}
                  onClick={() => setInviteOpen(true)}
                >
                  {t("myOrganization.staff.invite")}
                </CustomButton>
              )}
            </Box>
          }
        >
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" sx={{ color: colorTextMuted }}>
                {search
                  ? t("myOrganization.staff.noResults")
                  : t("myOrganization.staff.noStaff")}
              </Typography>
            </Box>
          ) : (
            <Table size="small" sx={{ mt: -1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colorTextMuted, fontWeight: 600, pl: 0 }}>
                    {t("myOrganization.staff.name")}
                  </TableCell>
                  <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                    {t("myOrganization.staff.email")}
                  </TableCell>
                  <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                    {t("myOrganization.staff.role")}
                  </TableCell>
                  <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                    {t("myOrganization.staff.phone")}
                  </TableCell>
                  <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                    {t("myOrganization.staff.restaurants")}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((staff) => (
                  <TableRow
                    key={staff.id}
                    onClick={() => navigate(staff.id)}
                    sx={{
                      cursor: "pointer",
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { backgroundColor: colorSurfaceHover },
                    }}
                  >
                    <TableCell sx={{ pl: 0, fontWeight: 500, color: colorTextPrimary }}>
                      {staff.firstName} {staff.lastName}
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted }}>{staff.email}</TableCell>
                    <TableCell sx={{ color: colorTextMuted }}>
                      {roleNameById(staff.roleId)}
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted }}>{staff.phone}</TableCell>
                    <TableCell sx={{ color: colorTextMuted }}>
                      {restaurantNamesByIds(staff.restaurantIds)}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0 }}>
                      <ChevronRightIcon sx={{ color: colorAccent, fontSize: 20 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </InfoCard>
      )}
      </Box>

      {organizationId && (
        <InviteStaffDialog
          open={inviteOpen}
          organizationId={organizationId}
          onClose={() => setInviteOpen(false)}
          onInvited={() => setSnackOpen(true)}
        />
      )}

      <SnackBar
        open={snackOpen}
        message={t("myOrganization.staff.inviteSuccess")}
        severity="success"
        onClose={() => setSnackOpen(false)}
      />
    </>
  );
};

export default StaffList;
